import { FC, memo, useEffect, useMemo, useState } from 'react'
import { Skeleton, useBreakpoint, useIsMobile } from '@brg/ui'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { CheckoutOuterMobileWrapper, CheckOutWrapper, FormTitle, PaymentMethods, PaymentModal, Summary, GiftMethodsWrapper, GiftMethodsContainer, GiftNextButtonPlace } from './styles'
import { OrderSummary } from '../OrderSummary/OrderSummary'
import { CheckOutModalProps } from './types'
import { PaymentFlowWrapper, NextButtonComponent } from '../PaymentManager/PaymentFlowWrapper'
import { PaymentMethodComponent } from './ui/PaymentMethodComponent'
import { isInsufficientFunds } from './helpers/validators'
import { useProfile } from '@brg/auth'
import { LayoutConst } from 'core'
import { FlowType } from "../../constants/flowType";
import { selectorSendGift } from "../../slices/SendGiftSlice";
import { useProductOrdering } from "../../hooks/useProductOrdering";
import { usePay } from "../../hooks/usePay";
import { useUserBalance } from "../../hooks/useUserBalance";
import { selectorOrder, setIsModerBuying } from "../../slices/OrderSlice";
import { EntityPaymentTypes } from "../../constants/payment";
import styled from "styled-components";
import { CurrencyType } from '@brg/api'
import { PaymentCurrency } from '../../constants/types'
import { getCurrencyTheme, getSortedCompositeCurrencyParts, isCompositeCurrency } from './helpers/utils'
import { NoWrapLabel, SummaryBigText } from '../OrderSummary/styles'

export const ModalClose = styled.div<any>`
    font-size: 75px;
    line-height: 1;
    cursor: pointer;
    transition: all 0.2s;
    color: #838382;
    position: absolute;
    top: 65px;
    right: 30px;
    font-weight: normal;
    &:hover {
        opacity: 0.9;
    }
`;


export interface CheckOutProps {
    handleClick?: (params?: any) => void
    onPaymentSuccess?: (params?: any) => void
    getCustomSummary?: (params?: any) => void
    onPaymentFail?: (params?: any) => void
    onNotEnoughMoney?: (params?: any) => void
    onNext?: () => void
    onPrev?: () => void
    isGift?: boolean
    payStep?: number
    competitionId?: string
    selectedProjectId?: string
}

export const CheckOut: FC<CheckOutProps> = memo((props) => {
    const { handleClick, onPaymentSuccess, getCustomSummary, onPaymentFail, onNotEnoughMoney, payStep, isGift, onNext, onPrev, competitionId, selectedProjectId } = props
    const { t } = useTranslation()
    const order = useSelector(selectorOrder)
    const gift = useSelector(selectorSendGift)
    const profile = useProfile()
    const isMobile = useBreakpoint(LayoutConst.screen.s)
    const isTablet = useBreakpoint(LayoutConst.screen.l);
    const dispatch = useDispatch()
    const [disabledHint, setDisabledHint] = useState<string>("")
    const { userBalance } = useUserBalance()
    const { isOrderChecking, pay, paymentCurrency, setPaymentCurrency } = usePay({
        handleClick,
        isGift,
        onNotEnoughMoney,
        onPaymentFail,
        onPaymentSuccess,
        userBalance,
        competitionId,
        selectedProjectId,
    })
    const isComplexPrice = true
    const isModer = profile?.rolesList?.includes('moder')
    const isGalleryModer = isModer || profile?.rolesList?.includes('gallerymoder') //zelandmoder
    const isPayStep = (payStep !== undefined) && (payStep === order?.flowStep)
    const { paymentCurrencies } = useProductOrdering({ setPaymentCurrency, onPaymentFail, isTariffChoosing: order.flowType === FlowType.BUY_BOOK || order.flowType === FlowType.COPY_CARD, competitionId })
    const memoizedCustomSummary = useMemo(() => {
        return typeof (getCustomSummary) === 'function'
            ? <>{getCustomSummary({ paymentMethod: paymentCurrency, price: order?.price, pay })}</> //summaryRef ???
            : undefined
    }, [getCustomSummary, paymentCurrency, order?.price, pay])

    useEffect(() => {
        if (!isGalleryModer) dispatch(setIsModerBuying(false))
    }, [dispatch, isGalleryModer])

    const flowWrapperProps = {
        nextDisabled: !!disabledHint,
        disableHint: (typeof (disabledHint) === 'string' && t(disabledHint)) || '',
        hidePrev: true,
        hideNext: true,
        onPrev,
        onNext,
        onPay: pay,
        payTitle: <NoWrapLabel noMarginSide>{t('payment.go_to_payment')}</NoWrapLabel> as any,
        isPayStep,
        showSpinner: isOrderChecking,
        wrapperProps: {},
    }

    const checkoutDisabled = useMemo(() => {
        let isDisabled = true
        if (!order) {
            return isDisabled
        }
        isDisabled = false
        const isImmediatePurchase = [EntityPaymentTypes.card, EntityPaymentTypes.country, EntityPaymentTypes.print_card, EntityPaymentTypes.book].includes(order.immediatePurchaseProps?.paymentEntityType)
        const isPrintLikePurchase = (order?.products?.length === 1) && (order.products[0].type === EntityPaymentTypes.print)
        if (isImmediatePurchase || isPrintLikePurchase) {
            if (isInsufficientFunds({ paymentCurrency, userBalance })) {
                isDisabled = true
                setDisabledHint(t("payment.hints.insufficient_funds"))
            }
        }
        if (!isDisabled) {
            setDisabledHint("")
        }
        return isDisabled
    }, [order, isModer, isGalleryModer, isGift, gift.newContactNumber, t, paymentCurrency, userBalance])

    const PaymentMethodsComponent = ({ title }: { title?: string }) => {
        return <PaymentMethods isGift={true}>
            {title && <SummaryBigText isGift={true}>{title}</SummaryBigText>}
            {
                paymentCurrencies?.length
                    ? paymentCurrencies.map((item: PaymentCurrency & any, idx: number) => {
                        const currencyTheme = getCurrencyTheme(paymentCurrencies.length, idx)

                        if (isCompositeCurrency(item) || isComplexPrice) {
                            const maxPart = isComplexPrice ? null : getSortedCompositeCurrencyParts(item)[0]
                            const key = isComplexPrice ? item[0] : String(maxPart?.currencyType) + String(maxPart?.amount)
                            return <PaymentMethodComponent
                                key={key}
                                paymentItem={item}
                                //@ts-ignore    !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                selectedCurrency={paymentCurrency}
                                //@ts-ignore    !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                setSelectedCurrency={setPaymentCurrency}
                                currencyTheme={currencyTheme}
                                // @ts-ignore
                                isComplexPrice={isComplexPrice}
                                multiplePayments={paymentCurrencies?.length > 1}
                                competitionId={competitionId}
                                selectedProjectId={selectedProjectId}

                            />
                        }

                        if (item?.currencyType === undefined || item?.currencyType === null) return null;

                        // скрываем покупку рублями для всего, кроме продуктов карточек - временная заглушка
                        //@ts-ignore
                        const hasProductCard = order?.products?.filter?.(item => item?.typeCard === "product")?.length > 0
                        if (item?.currencyType === CurrencyType.RUB && !hasProductCard) return null

                        return <PaymentMethodComponent
                            key={'' + item?.currencyType + item.countryId + item.amount}
                            paymentItem={item}
                            //@ts-ignore    !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                            selectedCurrency={paymentCurrency}
                            //@ts-ignore    !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                            setSelectedCurrency={setPaymentCurrency}
                            currencyTheme={currencyTheme}
                            competitionId={competitionId}
                            selectedProjectId={selectedProjectId}
                        />
                    })
                    : <Skeleton width={'100%'} height={136} />
            }
        </PaymentMethods>
    }

    return <CheckoutOuterMobileWrapper>
        <FormTitle>{t('payment.placing_order')}</FormTitle>
        <PaymentFlowWrapper {...flowWrapperProps}>
            <CheckOutWrapper
                mobile={isMobile || isTablet}
                mobileDir={'column'}
            >
                {/* @ts-ignore */}
                {memoizedCustomSummary || <Summary isGift={true}><OrderSummary paymentCurrency={paymentCurrency} /></Summary>}
                {
                    <GiftMethodsContainer>
                        <GiftMethodsWrapper><PaymentMethodsComponent title={t('payment.choose_payment_method')} /></GiftMethodsWrapper>
                        <GiftNextButtonPlace><NextButtonComponent {...flowWrapperProps} fullWidth={true} hideNext={false} /></GiftNextButtonPlace>
                    </GiftMethodsContainer>
                }
            </CheckOutWrapper>
        </PaymentFlowWrapper>
    </CheckoutOuterMobileWrapper>
})

// используется на зеландии и там, где нужно передавать внешний CheckOut
export const CheckOutModal: FC<CheckOutModalProps> = ({ isOpen, close, product = {}, productId, paymentEntityType, onPaymentSuccess, handleClick, children, competitionId, selectedProjectId }) => {
    const isMobile = useIsMobile()

    const onClose = () => {
        console.log('onClose');
        close && close()
    }

    return (
        <PaymentModal allowScroll isOpen={isOpen} onBackgroundClick={onClose}>
            {!isMobile && <ModalClose onClick={onClose}>&times;</ModalClose>}
            {
                children || <CheckOut onPaymentSuccess={onPaymentSuccess} handleClick={handleClick} competitionId={competitionId} selectedProjectId={selectedProjectId} />
            }
        </PaymentModal>
    )
}
import { FC, memo, useEffect, useMemo, useState } from 'react'
import { Skeleton, useBreakpoint, useIsMobile } from '@brg/ui'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { CheckoutOuterMobileWrapper, CheckOutWrapper, FormTitle, PaymentMethods, PaymentModal, Summary, GiftMethodsWrapper, GiftMethodsContainer, GiftNextButtonPlace } from './styles'
import { OrderSummary } from '../OrderSummary/OrderSummary'
import { CheckOutModalProps } from './types'
import { PaymentFlowWrapper, NextButtonComponent } from '../PaymentManager/PaymentFlowWrapper'
import { PaymentMethodComponent } from './ui/PaymentMethodComponent'
import { isInsufficientFunds } from './helpers/validators'
import { useProfile } from '@brg/auth'
import { LayoutConst } from 'core'
import { FlowType } from "../../constants/flowType";
import { selectorSendGift } from "../../slices/SendGiftSlice";
import { useProductOrdering } from "../../hooks/useProductOrdering";
import { usePay } from "../../hooks/usePay";
import { useUserBalance } from "../../hooks/useUserBalance";
import { selectorOrder, setIsModerBuying } from "../../slices/OrderSlice";
import { EntityPaymentTypes } from "../../constants/payment";
import styled from "styled-components";
import { CurrencyType } from '@brg/api'
import { PaymentCurrency } from '../../constants/types'
import { getCurrencyTheme, getSortedCompositeCurrencyParts, isCompositeCurrency } from './helpers/utils'
import { NoWrapLabel, SummaryBigText } from '../OrderSummary/styles'

export const ModalClose = styled.div<any>`
    font-size: 75px;
    line-height: 1;
    cursor: pointer;
    transition: all 0.2s;
    color: #838382;
    position: absolute;
    top: 65px;
    right: 30px;
    font-weight: normal;
    &:hover {
        opacity: 0.9;
    }
`;


export interface CheckOutProps {
    handleClick?: (params?: any) => void
    onPaymentSuccess?: (params?: any) => void
    getCustomSummary?: (params?: any) => void
    onPaymentFail?: (params?: any) => void
    onNotEnoughMoney?: (params?: any) => void
    onNext?: () => void
    onPrev?: () => void
    isGift?: boolean
    payStep?: number
    competitionId?: string
    selectedProjectId?: string
}

export const CheckOut: FC<CheckOutProps> = memo((props) => {
    const { handleClick, onPaymentSuccess, getCustomSummary, onPaymentFail, onNotEnoughMoney, payStep, isGift, onNext, onPrev, competitionId, selectedProjectId } = props
    const { t } = useTranslation()
    const order = useSelector(selectorOrder)
    const gift = useSelector(selectorSendGift)
    const profile = useProfile()
    const isMobile = useBreakpoint(LayoutConst.screen.s)
    const isTablet = useBreakpoint(LayoutConst.screen.l);
    const dispatch = useDispatch()
    const [disabledHint, setDisabledHint] = useState<string>("")
    const { userBalance } = useUserBalance()
    const { isOrderChecking, pay, paymentCurrency, setPaymentCurrency } = usePay({
        handleClick,
        isGift,
        onNotEnoughMoney,
        onPaymentFail,
        onPaymentSuccess,
        userBalance,
        competitionId,
        selectedProjectId,
    })
    const isComplexPrice = true
    const isModer = profile?.rolesList?.includes('moder')
    const isGalleryModer = isModer || profile?.rolesList?.includes('gallerymoder') //zelandmoder
    const isPayStep = (payStep !== undefined) && (payStep === order?.flowStep)
    const { paymentCurrencies } = useProductOrdering({ setPaymentCurrency, onPaymentFail, isTariffChoosing: order.flowType === FlowType.BUY_BOOK || order.flowType === FlowType.COPY_CARD, competitionId })
    const memoizedCustomSummary = useMemo(() => {
        return typeof (getCustomSummary) === 'function'
            ? <>{getCustomSummary({ paymentMethod: paymentCurrency, price: order?.price, pay })}</> //summaryRef ???
            : undefined
    }, [getCustomSummary, paymentCurrency, order?.price, pay])

    useEffect(() => {
        if (!isGalleryModer) dispatch(setIsModerBuying(false))
    }, [dispatch, isGalleryModer])

    const flowWrapperProps = {
        nextDisabled: !!disabledHint,
        disableHint: (typeof (disabledHint) === 'string' && t(disabledHint)) || '',
        hidePrev: true,
        hideNext: true,
        onPrev,
        onNext,
        onPay: pay,
        payTitle: <NoWrapLabel noMarginSide>{t('payment.go_to_payment')}</NoWrapLabel> as any,
        isPayStep,
        showSpinner: isOrderChecking,
        wrapperProps: {},
    }

    const checkoutDisabled = useMemo(() => {
        let isDisabled = true
        if (!order) {
            return isDisabled
        }
        isDisabled = false
        const isImmediatePurchase = [EntityPaymentTypes.card, EntityPaymentTypes.country, EntityPaymentTypes.print_card, EntityPaymentTypes.book].includes(order.immediatePurchaseProps?.paymentEntityType)
        const isPrintLikePurchase = (order?.products?.length === 1) && (order.products[0].type === EntityPaymentTypes.print)
        if (isImmediatePurchase || isPrintLikePurchase) {
            if (isInsufficientFunds({ paymentCurrency, userBalance })) {
                isDisabled = true
                setDisabledHint(t("payment.hints.insufficient_funds"))
            }
        }
        if (!isDisabled) {
            setDisabledHint("")
        }
        return isDisabled
    }, [order, isModer, isGalleryModer, isGift, gift.newContactNumber, t, paymentCurrency, userBalance])

    const PaymentMethodsComponent = ({ title }: { title?: string }) => {
        return <PaymentMethods isGift={true}>
            {title && <SummaryBigText isGift={true}>{title}</SummaryBigText>}
            {
                paymentCurrencies?.length
                    ? paymentCurrencies.map((item: PaymentCurrency & any, idx: number) => {
                        const currencyTheme = getCurrencyTheme(paymentCurrencies.length, idx)

                        if (isCompositeCurrency(item) || isComplexPrice) {
                            const maxPart = isComplexPrice ? null : getSortedCompositeCurrencyParts(item)[0]
                            const key = isComplexPrice ? item[0] : String(maxPart?.currencyType) + String(maxPart?.amount)
                            return <PaymentMethodComponent
                                key={key}
                                paymentItem={item}
                                //@ts-ignore    !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                selectedCurrency={paymentCurrency}
                                //@ts-ignore    !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                setSelectedCurrency={setPaymentCurrency}
                                currencyTheme={currencyTheme}
                                // @ts-ignore
                                isComplexPrice={isComplexPrice}
                                multiplePayments={paymentCurrencies?.length > 1}
                                competitionId={competitionId}
                                selectedProjectId={selectedProjectId}

                            />
                        }

                        if (item?.currencyType === undefined || item?.currencyType === null) return null;

                        // скрываем покупку рублями для всего, кроме продуктов карточек - временная заглушка
                        //@ts-ignore
                        const hasProductCard = order?.products?.filter?.(item => item?.typeCard === "product")?.length > 0
                        if (item?.currencyType === CurrencyType.RUB && !hasProductCard) return null

                        return <PaymentMethodComponent
                            key={'' + item?.currencyType + item.countryId + item.amount}
                            paymentItem={item}
                            //@ts-ignore    !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                            selectedCurrency={paymentCurrency}
                            //@ts-ignore    !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                            setSelectedCurrency={setPaymentCurrency}
                            currencyTheme={currencyTheme}
                            competitionId={competitionId}
                            selectedProjectId={selectedProjectId}
                        />
                    })
                    : <Skeleton width={'100%'} height={136} />
            }
        </PaymentMethods>
    }

    return <CheckoutOuterMobileWrapper>
        <FormTitle>{t('payment.placing_order')}</FormTitle>
        <PaymentFlowWrapper {...flowWrapperProps}>
            <CheckOutWrapper
                mobile={isMobile || isTablet}
                mobileDir={'column'}
            >
                {/* @ts-ignore */}
                {memoizedCustomSummary || <Summary isGift={true}><OrderSummary paymentCurrency={paymentCurrency} /></Summary>}
                {
                    <GiftMethodsContainer>
                        <GiftMethodsWrapper><PaymentMethodsComponent title={t('payment.choose_payment_method')} /></GiftMethodsWrapper>
                        <GiftNextButtonPlace><NextButtonComponent {...flowWrapperProps} fullWidth={true} hideNext={false} /></GiftNextButtonPlace>
                    </GiftMethodsContainer>
                }
            </CheckOutWrapper>
        </PaymentFlowWrapper>
    </CheckoutOuterMobileWrapper>
})

// используется на зеландии и там, где нужно передавать внешний CheckOut
export const CheckOutModal: FC<CheckOutModalProps> = ({ isOpen, close, product = {}, productId, paymentEntityType, onPaymentSuccess, handleClick, children, competitionId, selectedProjectId }) => {
    const isMobile = useIsMobile()

    const onClose = () => {
        console.log('onClose');
        close && close()
    }

    return (
        <PaymentModal allowScroll isOpen={isOpen} onBackgroundClick={onClose}>
            {!isMobile && <ModalClose onClick={onClose}>&times;</ModalClose>}
            {
                children || <CheckOut onPaymentSuccess={onPaymentSuccess} handleClick={handleClick} competitionId={competitionId} selectedProjectId={selectedProjectId} />
            }
        </PaymentModal>
    )
}
